import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, VStack, HStack, Text, Button, Icon, Badge, Switch, Select, Table, Thead,
  Tbody, Tr, Th, Td, Input, Textarea, useToast, useColorModeValue, Heading,
  Spinner, Divider, Accordion, AccordionItem, AccordionButton, AccordionPanel,
  AccordionIcon, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalFooter, ModalCloseButton, useDisclosure, FormControl, FormLabel, Code,
} from '@chakra-ui/react';
import { FiDatabase, FiArrowRight, FiPlus, FiPlay, FiEdit2 } from 'react-icons/fi';
import { httpsCallable } from 'firebase/functions';
import { functionsUsEast1 } from '../../../utils/init-firebase';

const SYSTEM_LABELS = {
  arive: 'Arive LOS',
  clients: 'Hub Clients',
  lead_webhook: 'Lead Webhooks',
  dialpad: 'Dialpad',
  origin: 'Origin CRM',
};

const ENTITY_LABELS = {
  loan: 'Loan',
  lead: 'Lead',
  client: 'Client',
  deal: 'Deal',
  contact: 'Contact',
  call: 'Call',
};

const CATEGORY_ORDER = ['borrower', 'property', 'loan', 'financial', 'stage', 'marketing', 'parties', 'system'];

function formatLabel(system, entity) {
  return `${SYSTEM_LABELS[system] || system} - ${ENTITY_LABELS[entity] || entity}`;
}

export default function FieldMappingManager() {
  const [mappings, setMappings] = useState([]);
  const [pairs, setPairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPair, setSelectedPair] = useState('');
  const [testJson, setTestJson] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const toast = useToast();

  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const headerBg = useColorModeValue('gray.50', 'gray.700');

  const { isOpen: isAddOpen, onOpen: onAddOpen, onClose: onAddClose } = useDisclosure();
  const [newMapping, setNewMapping] = useState({
    source_system: '', source_entity: '', source_field: '',
    target_system: 'origin', target_entity: 'deal', target_field: '',
    transform_type: 'direct', transform_config: '{}',
    direction: 'source_to_target', category: 'loan', description: '',
    overwrite_if_exists: false,
  });

  const loadMappings = useCallback(async () => {
    setLoading(true);
    try {
      const fn = httpsCallable(functionsUsEast1, 'getFieldMappings');
      const filter = {};
      if (selectedPair) {
        const [ss, se, ts, te] = selectedPair.split(':');
        filter.sourceSystem = ss;
        filter.sourceEntity = se;
        filter.targetSystem = ts;
        filter.targetEntity = te;
      }
      const result = await fn(filter);
      setMappings(result.data.mappings || []);
      setPairs(result.data.pairs || []);
    } catch (err) {
      toast({ title: 'Failed to load mappings', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setLoading(false);
    }
  }, [selectedPair, toast]);

  useEffect(() => { loadMappings(); }, [loadMappings]);

  const toggleMapping = async (id, currentActive) => {
    try {
      const fn = httpsCallable(functionsUsEast1, 'updateFieldMapping');
      await fn({ id, updates: { is_active: !currentActive } });
      loadMappings();
    } catch (err) {
      toast({ title: 'Update failed', description: err.message, status: 'error', duration: 3000 });
    }
  };

  const handleAddMapping = async () => {
    try {
      const fn = httpsCallable(functionsUsEast1, 'createFieldMapping');
      const mapping = { ...newMapping, transform_config: JSON.parse(newMapping.transform_config) };
      await fn({ mapping });
      toast({ title: 'Mapping created', status: 'success', duration: 3000 });
      onAddClose();
      loadMappings();
    } catch (err) {
      toast({ title: 'Create failed', description: err.message, status: 'error', duration: 5000 });
    }
  };

  const handleTest = async () => {
    if (!testJson.trim() || !selectedPair) return;
    setTestLoading(true);
    setTestResult(null);
    try {
      const [ss, se, ts] = selectedPair.split(':');
      const fn = httpsCallable(functionsUsEast1, 'testFieldMapping');
      const result = await fn({
        sourceRecord: JSON.parse(testJson),
        sourceSystem: ss,
        sourceEntity: se,
        targetSystem: ts,
      });
      setTestResult(result.data.result);
    } catch (err) {
      toast({ title: 'Test failed', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setTestLoading(false);
    }
  };

  const grouped = {};
  for (const m of mappings) {
    const cat = m.category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(m);
  }

  const sortedCategories = Object.keys(grouped).sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a);
    const bi = CATEGORY_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return (
    <VStack align="stretch" spacing={5}>
      <HStack justify="space-between">
        <VStack align="start" spacing={0}>
          <Heading size="md">Field Mappings</Heading>
          <Text fontSize="sm" color={mutedColor}>Define how fields map between external systems and Origin CRM</Text>
        </VStack>
        <HStack>
          <Badge colorScheme="blue" fontSize="xs">{mappings.length} mappings</Badge>
          <Button size="sm" leftIcon={<Icon as={FiPlus} />} colorScheme="blue" onClick={onAddOpen}>Add Mapping</Button>
        </HStack>
      </HStack>

      {/* Pair Selector */}
      <HStack>
        <Select size="sm" placeholder="All source/target pairs" value={selectedPair}
          onChange={(e) => setSelectedPair(e.target.value)} maxW="400px">
          {pairs.map((p, i) => {
            const key = `${p.source_system}:${p.source_entity}:${p.target_system}:${p.target_entity}`;
            return (
              <option key={i} value={key}>
                {formatLabel(p.source_system, p.source_entity)} → {formatLabel(p.target_system, p.target_entity)}
              </option>
            );
          })}
        </Select>
      </HStack>

      {loading ? <Spinner /> : (
        <Accordion allowMultiple defaultIndex={sortedCategories.map((_, i) => i)}>
          {sortedCategories.map((category) => (
            <AccordionItem key={category} border="none" mb={2}>
              <AccordionButton bg={headerBg} borderRadius="md" px={3} py={2}>
                <HStack flex="1">
                  <Text fontWeight="semibold" fontSize="sm" textTransform="capitalize">{category}</Text>
                  <Badge fontSize="10px" colorScheme="gray">{grouped[category].length}</Badge>
                </HStack>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel px={0} py={2}>
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th fontSize="10px">Source</Th>
                      <Th fontSize="10px" w="30px"></Th>
                      <Th fontSize="10px">Target</Th>
                      <Th fontSize="10px">Transform</Th>
                      <Th fontSize="10px">Dir</Th>
                      <Th fontSize="10px">Overwrite</Th>
                      <Th fontSize="10px">Active</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {grouped[category].map((m) => (
                      <Tr key={m.id} opacity={m.is_active ? 1 : 0.4}>
                        <Td fontSize="xs">
                          <Code fontSize="10px">{m.source_system}.{m.source_entity}.{m.source_field}</Code>
                        </Td>
                        <Td><Icon as={FiArrowRight} color={mutedColor} boxSize={3} /></Td>
                        <Td fontSize="xs">
                          <Code fontSize="10px">{m.target_system}.{m.target_entity}.{m.target_field}</Code>
                        </Td>
                        <Td>
                          <Badge fontSize="9px" colorScheme={m.transform_type === 'direct' ? 'gray' : 'purple'}>
                            {m.transform_type}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge fontSize="9px" colorScheme={m.direction === 'both' ? 'green' : 'blue'}>
                            {m.direction === 'both' ? '↔' : '→'}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge fontSize="9px" colorScheme={m.overwrite_if_exists ? 'orange' : 'gray'}>
                            {m.overwrite_if_exists ? 'yes' : 'no'}
                          </Badge>
                        </Td>
                        <Td>
                          <Switch size="sm" isChecked={m.is_active} onChange={() => toggleMapping(m.id, m.is_active)} />
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      )}

      {/* Test Tool */}
      {selectedPair && (
        <>
          <Divider />
          <VStack align="stretch" spacing={2}>
            <Heading size="sm">Test Mapping</Heading>
            <Text fontSize="xs" color={mutedColor}>Paste a source JSON record to see what the engine produces</Text>
            <Textarea
              size="sm" fontFamily="mono" fontSize="xs" rows={6}
              placeholder='{"base_loan_amount": 450000, "note_rate": 6.5, "borrower_first_name": "John"}'
              value={testJson} onChange={(e) => setTestJson(e.target.value)}
            />
            <Button size="sm" leftIcon={<Icon as={FiPlay} />} colorScheme="green" onClick={handleTest}
              isLoading={testLoading} isDisabled={!testJson.trim()}>
              Run Test
            </Button>
            {testResult && (
              <Box bg={headerBg} p={3} borderRadius="md" fontSize="xs" fontFamily="mono">
                <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(testResult, null, 2)}</pre>
              </Box>
            )}
          </VStack>
        </>
      )}

      {/* Add Mapping Modal */}
      <Modal isOpen={isAddOpen} onClose={onAddClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Field Mapping</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3}>
              <HStack w="100%">
                <FormControl><FormLabel fontSize="xs">Source System</FormLabel>
                  <Input size="sm" value={newMapping.source_system} onChange={(e) => setNewMapping({ ...newMapping, source_system: e.target.value })} placeholder="arive" />
                </FormControl>
                <FormControl><FormLabel fontSize="xs">Source Entity</FormLabel>
                  <Input size="sm" value={newMapping.source_entity} onChange={(e) => setNewMapping({ ...newMapping, source_entity: e.target.value })} placeholder="loan" />
                </FormControl>
              </HStack>
              <FormControl><FormLabel fontSize="xs">Source Field</FormLabel>
                <Input size="sm" value={newMapping.source_field} onChange={(e) => setNewMapping({ ...newMapping, source_field: e.target.value })} placeholder="base_loan_amount" />
              </FormControl>
              <HStack w="100%">
                <FormControl><FormLabel fontSize="xs">Target System</FormLabel>
                  <Input size="sm" value={newMapping.target_system} onChange={(e) => setNewMapping({ ...newMapping, target_system: e.target.value })} />
                </FormControl>
                <FormControl><FormLabel fontSize="xs">Target Entity</FormLabel>
                  <Input size="sm" value={newMapping.target_entity} onChange={(e) => setNewMapping({ ...newMapping, target_entity: e.target.value })} />
                </FormControl>
              </HStack>
              <FormControl><FormLabel fontSize="xs">Target Field</FormLabel>
                <Input size="sm" value={newMapping.target_field} onChange={(e) => setNewMapping({ ...newMapping, target_field: e.target.value })} placeholder="amount" />
              </FormControl>
              <HStack w="100%">
                <FormControl><FormLabel fontSize="xs">Transform</FormLabel>
                  <Select size="sm" value={newMapping.transform_type} onChange={(e) => setNewMapping({ ...newMapping, transform_type: e.target.value })}>
                    <option value="direct">direct</option>
                    <option value="value_map">value_map</option>
                    <option value="format_phone">format_phone</option>
                    <option value="format_date">format_date</option>
                    <option value="parse_number">parse_number</option>
                    <option value="concat">concat</option>
                    <option value="split">split</option>
                    <option value="to_lower">to_lower</option>
                    <option value="default">default</option>
                  </Select>
                </FormControl>
                <FormControl><FormLabel fontSize="xs">Category</FormLabel>
                  <Input size="sm" value={newMapping.category} onChange={(e) => setNewMapping({ ...newMapping, category: e.target.value })} placeholder="loan" />
                </FormControl>
              </HStack>
              <FormControl><FormLabel fontSize="xs">Description</FormLabel>
                <Input size="sm" value={newMapping.description} onChange={(e) => setNewMapping({ ...newMapping, description: e.target.value })} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAddClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={handleAddMapping}>Create</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
}
